import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  BarChart3,
  Bell,
  LogIn,
  LogOut,
  Menu,
  Moon,
  Newspaper,
  Radar,
  Settings,
  ShieldCheck,
  Star,
  Sun,
  Users,
  LayoutGrid,
  X,
} from "lucide-react";
import { api, apiBase, setAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BoursicoteLogo } from "@/components/BoursicoteLogo";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/forums", label: "Forums", icon: LayoutGrid },
  { to: "/signals", label: "Signaux", icon: Radar },
  { to: "/news", label: "Actualités", icon: Newspaper },
  { to: "/users", label: "Utilisateurs", icon: Users },
  { to: "/follows", label: "Mes suivis", icon: Star },
  { to: "/alerts", label: "Alertes", icon: Bell },
  { to: "/stats", label: "Statistiques", icon: BarChart3 },
  { to: "/account", label: "Compte", icon: Settings },
];

// ─────────────────────── AppLayout ───────────────────────
//
// Responsive shell :
//   < md (< 768 px) : top-bar with burger → drawer that slides in from the left
//   md (≥ 768)      : 64 px icon-only sidebar fixed on the left (tooltips on hover)
//   lg (≥ 1024)     : 240 px full sidebar with labels
//
// The drawer is closed automatically on route change so navigation feels
// instantaneous on phones.

export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer when navigating, otherwise it lingers on top of the new page.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex flex-col md:grid md:grid-cols-[240px_1fr] min-h-screen md:h-screen">
      {/* ── Mobile top-bar ─────────────────────── */}
      <header className="md:hidden flex items-center justify-between gap-2 h-14 px-3 border-b border-border bg-surface shrink-0">
        <div className="flex items-center gap-2">
          <DialogPrimitive.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DialogPrimitive.Trigger asChild>
              <Button size="icon" variant="ghost" aria-label="Ouvrir le menu">
                <Menu className="size-5" />
              </Button>
            </DialogPrimitive.Trigger>
            <MobileDrawer />
          </DialogPrimitive.Root>
          <Link to="/" aria-label="Retour à l'accueil">
            <BoursicoteLogo variant="wordmark" />
          </Link>
        </div>
        <FooterActions compact />
      </header>

      {/* ── Desktop sidebar (md+ : 240 px, always icon + label) ── */}
      <aside className="hidden md:flex md:flex-col border-r border-border bg-surface">
        <div className="h-14 px-4 flex items-center border-b border-border">
          <Link to="/" aria-label="Retour à l'accueil">
            <BoursicoteLogo variant="wordmark" />
          </Link>
        </div>
        <SidebarNav />
        <UserFooter />
      </aside>

      {/* ── Main content ─────────────────────── */}
      <main className="overflow-auto bg-page min-h-0">
        <div className="max-w-screen-2xl mx-auto p-3 sm:p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// ─────────────────────── Mobile drawer ───────────────────────

function MobileDrawer() {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        data-bourso-anim="overlay"
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
      />
      <DialogPrimitive.Content
        data-bourso-anim="drawer"
        aria-label="Menu de navigation"
        className="fixed inset-y-0 left-0 z-50 w-72 bg-surface border-r border-border-strong flex flex-col"
      >
        <div className="h-14 px-4 flex items-center justify-between border-b border-border">
          <Link to="/" aria-label="Retour à l'accueil">
            <BoursicoteLogo variant="wordmark" />
          </Link>
          <DialogPrimitive.Close asChild>
            <Button size="icon" variant="ghost" aria-label="Fermer le menu">
              <X className="size-5" />
            </Button>
          </DialogPrimitive.Close>
        </div>
        <SidebarNav />
        <UserFooter />
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ─────────────────────── Shared sub-components ───────────────────────

/**
 * Vertical nav list — icon + label side-by-side on every breakpoint.
 * The same component is used in the mobile drawer and in the desktop
 * sidebar (always 240 px on md+).
 */
function SidebarNav() {
  // Admin entry only for allow-listed accounts (server enforces it anyway).
  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
    staleTime: 60_000,
  });
  const items = meData?.user?.is_admin
    ? [...NAV, { to: "/admin", label: "Admin", icon: ShieldCheck }]
    : NAV;
  return (
    <nav className="flex-1 p-2 flex flex-col gap-1 overflow-y-auto">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            cn(
              // Accent bar (::before) is always present but scales from 0 height
              // when idle → full height on hover/active, giving a smooth slide-in.
              "relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
              "before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-primary before:origin-center before:scale-y-0 before:transition-transform",
              isActive
                ? "bg-hover text-primary font-semibold before:scale-y-100"
                : "text-foreground-muted hover:bg-hover hover:text-foreground hover:before:scale-y-100"
            )
          }
        >
          <Icon className="size-5 shrink-0" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** Full footer with email + theme + sign-out, used in lg sidebar + drawer. */
function UserFooter() {
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
    staleTime: 60_000,
  });
  const user = meData?.user ?? null;

  return (
    <div className="p-3 border-t border-border flex flex-col gap-2">
      {user ? (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="truncate text-foreground-muted" title={user.email}>
            {user.email}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              title={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
              onClick={toggleTheme}
            >
              {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              title="Se déconnecter"
              onClick={signOut}
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/login")}
            className="flex-1"
          >
            <LogIn className="size-3.5" />
            Se connecter
          </Button>
          <Button
            size="icon"
            variant="ghost"
            title={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
            onClick={toggleTheme}
          >
            {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </Button>
        </div>
      )}
      <div className="text-xs text-foreground-subtle">
        v0.1 · {new Date().getFullYear()}
      </div>
    </div>
  );
}

/**
 * The two utility actions (theme + auth) inlined into the mobile top-bar.
 * `compact` skips the email label so we don't push the burger off-screen.
 */
function FooterActions({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
    staleTime: 60_000,
  });
  const user = meData?.user ?? null;

  return (
    <div className="flex items-center gap-1">
      {!compact && user && (
        <span
          className="truncate text-xs text-foreground-muted max-w-[10rem]"
          title={user.email}
        >
          {user.email}
        </span>
      )}
      <Button
        size="icon"
        variant="ghost"
        aria-label={isDark ? "Passer en mode clair" : "Passer en mode sombre"}
        onClick={toggleTheme}
      >
        {isDark ? <Sun className="size-5" /> : <Moon className="size-5" />}
      </Button>
      {user ? (
        <Button
          size="icon"
          variant="ghost"
          aria-label="Se déconnecter"
          onClick={signOut}
        >
          <LogOut className="size-5" />
        </Button>
      ) : (
        <Button
          size="icon"
          variant="ghost"
          aria-label="Se connecter"
          onClick={() => navigate("/login")}
        >
          <LogIn className="size-5" />
        </Button>
      )}
    </div>
  );
}

async function signOut() {
  try {
    await fetch(`${apiBase}/api/auth/sign-out`, { method: "POST", credentials: "include" });
  } catch {
    /* ignore */
  }
  setAuthToken(null);
  window.location.assign("/login");
}
